import { startCategoryId } from "@/domain/default_category";
import type { Table } from "dexie";
import { readCachedEntitlement } from "./entitlement";
import { parseFamilyView } from "./family";
/**
 * The repository — every write in the app goes through here.
 *
 * Components never touch Dexie or the backend client directly (W-1). They call these
 * functions, which write locally and *queue* a sync. Nothing here awaits the
 * network, which is what makes INV-4 true rather than aspirational.
 */

import { validateFamilyResponse, type FamilyView } from "./backend";
import type { LedgerRef } from "./ledger";
import { notificationNeed } from "./notification_need";
import { DEFAULT_ICON_BY_NAME, isSingleEmoji } from "@/domain/category_icon";
import { EXPENDITURE_LABEL, SAVINGS_LABEL, isSavingsCategory } from "@/domain/category_kind";
import { cents, MAX_AMOUNT_CENTS, formatMoney, type Cents, type RateNano } from "@/domain/money";
import { computeBalances, computeShares, toBaseShares, myShareHome, SplitRowError, MissingHomeRateError, assertSplitOccurredAt } from "@/domain/splits";
import { validateSplit, validateTransfer } from "../../shared/split_rules";
import { versionAt } from "@/domain/versions";
import { parseYmd, periodBounds, periodStartDate, zonedMidnight } from "@/domain/period";
import { carryAcrossBoundary, carriedBoundaryVersion, selectedBoundaryVersion, redundantBoundaryVersions } from "@/domain/boundary_carry";
import { defaultProfile } from "./profile_defaults";
import { salaryInForce } from "@/domain/salary";
import { db as personalDb, familyDb, newId, nowIso } from "./db";
import { isPushInFlight, queueSync, FAMILY_VIEW_KEY } from "./sync";
import type {
  LocalSplitGroup,
  LocalSplitExpense,
  LocalSplitTransfer,
  SplitExpense,
  SplitTransfer,
  Budget,
  Category,
  Expense,
  LocalBudget,
  LocalCategory,
  LocalExpense,
  LocalRecurringRule,
  Profile,
  RecurringRule,
  Salary,
  SyncMeta,
} from "./types";
import { fromWireSplitGroup, fromWireSplitExpense, fromWireSplitTransfer } from "./types";
import { readLinkedList } from "./linked_splits";

/**
 * The signed-in user's id, cached so writes need no async lookup.
 * Set by the auth layer on every session change.
 */
let userId: string | null = null;

export function setCurrentUser(id: string | null): void {
  userId = id;
}

export class FamilyReadOnlyError extends Error {
  constructor() { super("Read-only: your plan has ended."); this.name = "FamilyReadOnlyError"; }
}
export function assertLedgerWritable(ledger?: LedgerRef): void {
  if (ledger?.kind === "family" && userId && readCachedEntitlement(userId)?.entitlement.readOnly) throw new FamilyReadOnlyError();
}

export function requireUser(ledger?: LedgerRef): string {
  assertLedgerWritable(ledger);
  if (ledger) return ledger.ownerId;
  if (!userId) {
    // Fail loud (INV-7). A write with no user would be rejected by the Worker
    // anyway, but only at the network boundary — long after the UI said "saved".
    throw new Error("No signed-in user. Writes are not possible before sign-in.");
  }
  return userId;
}

const DIRTY: SyncMeta = { _dirty: 1, _syncedAt: null };

/* ── undo ──────────────────────────────────────────────────────────────────── */

/**
 * A snapshot of rows as they were BEFORE a destructive action, so a ~5-second
 * Undo can put them back. Restoring writes the prior values with a fresh
 * `updatedAt`, so last-write-wins carries the undo across devices too — a
 * tombstone is un-set the same way it was set, never by a hard resurrection (INV-5).
 */
export interface UndoSnapshot {
  categories: LocalCategory[];
  expenses: LocalExpense[];
  budgets: LocalBudget[];
  recurringRules: LocalRecurringRule[];
}

/** Reverse a delete captured in an `UndoSnapshot`, as new writes that win on `updatedAt`. */
export async function restoreRows(snapshot: UndoSnapshot, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.categories, db.expenses, db.budgets, db.recurringRules, async () => {
    for (const category of snapshot.categories) {
      await db.categories.put({ ...category, updatedAt: nextUpdatedAt((await db.categories.get(category.id))?.updatedAt ?? category.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
    for (const expense of snapshot.expenses) {
      await db.expenses.put({ ...expense, updatedAt: nextUpdatedAt((await db.expenses.get(expense.id))?.updatedAt ?? expense.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
    for (const budget of snapshot.budgets) {
      await db.budgets.put({ ...budget, updatedAt: nextUpdatedAt((await db.budgets.get(budget.id))?.updatedAt ?? budget.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
    for (const rule of snapshot.recurringRules) {
      await db.recurringRules.put({ ...rule, updatedAt: nextUpdatedAt((await db.recurringRules.get(rule.id))?.updatedAt ?? rule.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
}

/* ── expenses ──────────────────────────────────────────────────────────────── */

export interface NewExpense {
  categoryId: string;
  amountCents: Cents;
  /** Defaults to now. Set explicitly when back-dating from history. */
  occurredAt?: string;
  note?: string | null;
  recurringRuleId?: string | null;
  /** The `soundingActions` row this came from, when the agent wrote it. */
  sourceId?: string | null;
}

/**
 * Log an expense. Returns as soon as it is on disk — no network, no spinner.
 * This is the function the whole product is built around.
 */
export async function addExpense(input: NewExpense, ledger?: LedgerRef): Promise<Expense> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  // Nothing may be logged against a savings category — every total in the app
  // filters those rows out, so accepting the write would store an expense that
  // changes no figure on any screen and says nothing about why. The numpad
  // already cannot offer one, but two callers reach here without it: a recurring
  // rule whose category was marked savings afterwards, and the agent, whose
  // category list is built server-side. Refusing at the chokepoint covers both,
  // and every future caller (INV-7, C-8).
  //
  // One indexed local read, on a path that must never await the network — it
  // does not, and IndexedDB is what the write itself goes to anyway (INV-4).
  const category = await db.categories.get(input.categoryId);
  if (category && isSavingsCategory(category)) {
    throw new Error(
      `${category.name} is a savings category, so nothing can be logged against it. ` +
        `Change its amount in Settings, or pick a spending category.`,
    );
  }

  let memberId: string | undefined;
  if (ledger?.kind === "family") {
    const cache = await personalDb.meta.get(FAMILY_VIEW_KEY);
    if (cache) {
      const family: unknown = JSON.parse(cache.value);
      validateFamilyResponse({ family }, "/", "GET");
      const membership = family as FamilyView;
      if (membership.family_id !== ledger.ownerId) throw new Error("Family membership does not match this ledger.");
      memberId = membership.my_member_id;
    }
  }
  const now = nowIso();
  const row: LocalExpense = {
    id: newId(),
    userId: requireUser(ledger),
    categoryId: input.categoryId,
    amountCents: input.amountCents,
    occurredAt: input.occurredAt ?? now,
    note: input.note ?? null,
    recurringRuleId: input.recurringRuleId ?? null,
    sourceId: input.sourceId ?? null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    ...DIRTY,
    ...(memberId ? { _createdBy: memberId, _updatedBy: memberId } : {}),
  };

  await db.expenses.put(row);
  queueSync();
  return row;
}

export async function updateExpense(
  id: string,
  patch: Partial<Pick<Expense, "categoryId" | "amountCents" | "occurredAt" | "note">>,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.expenses.get(id);
  if (!existing) throw new Error(`Expense ${id} not found`);

  requireUser(ledger);
  if (await isLockedSplitShare(existing, ledger)) {
    if (patch.occurredAt !== undefined && patch.occurredAt !== existing.occurredAt) throw new SplitExpenseLockedError(existing);
    if (patch.amountCents !== undefined && patch.amountCents !== existing.amountCents) throw new SplitExpenseLockedError(existing);
    if (patch.categoryId !== undefined) {
      const target = await db.categories.get(patch.categoryId);
      if (ledger?.kind === "family" || !target || target.userId !== requireUser() || target.deletedAt !== null || target.archivedAt !== null || isSavingsCategory(target)) throw new SplitExpenseLockedError(existing);
    }
  }

  const cache = ledger?.kind === "family" ? await personalDb.meta.get(FAMILY_VIEW_KEY) : undefined;
  const member = cache ? parseFamilyView(JSON.parse(cache.value)).my_member_id : undefined;
  await db.expenses.put({ ...existing, ...patch, updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY, ...(member ? { _updatedBy: member } : {}) });
  queueSync();
}

/** Soft-delete (INV-5). A hard delete would be resurrected by the next device to sync. */
export async function deleteExpense(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.expenses.get(id);
  if (!existing) throw new Error(`Expense ${id} not found`);

  requireUser(ledger);
  if (await isLockedSplitShare(existing, ledger)) throw new SplitExpenseLockedError(existing);
  await db.expenses.put({ ...existing, deletedAt: nowIso(), updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

/**
 * Undo a just-logged expense.
 *
 * Removed outright ONLY when we can prove it never left this device: never
 * pushed, and no push currently in flight. Otherwise it becomes an ordinary
 * tombstone (INV-5).
 *
 * The in-flight check is load-bearing. `_syncedAt` is still null for the whole
 * duration of an upload, so without it a row that had just been accepted by the
 * server would be hard-deleted locally and resurrected by the next pull —
 * silently, with the user believing they had undone it.
 */
export async function undoExpense(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.expenses.get(id);
  if (!existing) return;

  if (await isLockedSplitShare(existing, ledger)) throw new SplitExpenseLockedError(existing);
  // Shared-ledger Undo always propagates a tombstone, even before the first upload.
  const neverLeft = existing._dirty === 1 && existing._syncedAt === null && !isPushInFlight(ledger);
  if (neverLeft && !existing.splitRef && ledger?.kind !== "family") {
    await db.expenses.delete(id); // invariant-exempt: INV-5 — proven never to have left this device
    return;
  }
  await deleteExpense(id, ledger);
}

/**
 * Tombstone several expenses at once (INV-5) — atomic in one transaction, one
 * queued sync. Used by the Ledger's multi-select. Returns the prior states so the
 * action can be undone (a hard delete would be resurrected by the next device to
 * sync). One unknown id aborts the whole transaction rather than half-deleting a set.
 */
export async function deleteExpenses(ids: readonly string[], ledger?: LedgerRef): Promise<UndoSnapshot> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  const undo: UndoSnapshot = { categories: [], expenses: [], budgets: [], recurringRules: [] };
  await db.transaction("rw", [db.expenses, db.splitGroups, db.splitExpenses, db.meta, db.linkedSplitRows], async () => {
    for (const id of ids) {
      const existing = await db.expenses.get(id);
      if (!existing) throw new Error(`Expense ${id} not found`);
      if (await isLockedSplitShare(existing, ledger)) throw new SplitExpenseLockedError(existing);
      undo.expenses.push(existing);
      await db.expenses.put({ ...existing, deletedAt: now, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
  return undo;
}

/**
 * Move several expenses to another category at once — a bulk `categoryId` edit,
 * atomic and one queued sync. The target must be a live category (INV-7): moving
 * expenses into a tombstoned or unknown category would render them
 * "Uncategorised", the exact orphaning tombstone-delete exists to prevent.
 */
export async function recategoriseExpenses(
  ids: readonly string[],
  categoryId: string,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", [db.expenses, db.categories, db.splitGroups, db.splitExpenses, db.meta, db.linkedSplitRows], async () => {
    const target = await db.categories.get(categoryId);
    if (!target || target.deletedAt !== null) {
      throw new Error(`Cannot recategorise into unknown or deleted category ${categoryId}`);
    }
    for (const id of ids) {
      const existing = await db.expenses.get(id);
      if (!existing) throw new Error(`Expense ${id} not found`);
      if (await isLockedSplitShare(existing, ledger) && (target.userId !== requireUser() || target.archivedAt !== null || isSavingsCategory(target))) throw new SplitExpenseLockedError(existing);
      await db.expenses.put({ ...existing, categoryId, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
}

/* ── categories ────────────────────────────────────────────────────────────── */

/**
 * The budget-sheet fields a category may carry.
 *
 * `note` and `keepInAccount` still round-trip and nothing else;
 * `savingsOrExpenditure` drives the expense ceiling (scope expansion
 * 2026-07-31), so a sheet import can now move a category out of the spending
 * history.
 */
export type CategorySheetFields = Pick<
  Category,
  "note" | "savingsOrExpenditure" | "keepInAccount"
>;

export async function addCategory(
  name: string,
  color: string,
  fields?: CategorySheetFields,
  ledger?: LedgerRef,
): Promise<Category> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const now = nowIso();
  if (ledger?.kind === "family" && fields && isSavingsCategory({ ...fields, isFallback: false })) throw new Error("Family categories must be expenditure.");
  const maxOrder = await db.categories.orderBy("sortOrder").last();
  const row = {
    id: newId(),
    userId: requireUser(ledger),
    name,
    color,
    sortOrder: (maxOrder?.sortOrder ?? -1) + 1,
    archivedAt: null,
    rolloverOverride: null,
    isFallback: false,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    note: fields?.note ?? null,
    // Explicit, never null: blank means *savings* now, and a category added from
    // the "add category" sheet is an expense category unless the user says
    // otherwise in the control on its row (`src/domain/category_kind.ts`).
    savingsOrExpenditure: fields?.savingsOrExpenditure ?? EXPENDITURE_LABEL,
    keepInAccount: fields?.keepInAccount ?? null,
    icon: null,
    ...DIRTY,
  };
  await db.categories.put(row);
  queueSync();
  return row;
}

/**
 * The eight defaults (SPEC.md §4). Colours from DESIGN_SYSTEM.md §2.3.
 *
 * Seeded client-side. This was a database trigger before the backend swap,
 * but the server stores rows opaquely and has no schema to construct one from —
 * so the client owns it.
 */
export const DEFAULT_CATEGORIES: readonly { name: string; color: string; isFallback: boolean }[] = [
  { name: "Food & Drink", color: "#FF8A5B", isFallback: false },
  { name: "Groceries", color: "#35B87E", isFallback: false },
  { name: "Transport", color: "#4B9FE1", isFallback: false },
  { name: "Bills", color: "#9B87F5", isFallback: false },
  { name: "Shopping", color: "#EC6FA9", isFallback: false },
  { name: "Fun", color: "#E8B33C", isFallback: false },
  { name: "Health", color: "#2FC4A8", isFallback: false },
  { name: "Other", color: "#8AA0AB", isFallback: true },
];

/**
 * Give a new account its starting categories.
 *
 * Idempotent and only ever fires on a genuinely empty table, so it cannot
 * duplicate an existing set — including one that arrived from another device
 * mid-sync. There is never an empty state in front of the first expense
 * (SPEC.md §9.5), and the numpad is unusable without at least one category.
 */
export async function seedDefaultCategoriesIfEmpty(ledger?: LedgerRef): Promise<boolean> {
  if (ledger?.kind === "family") return seedFamilyFallbackIfEmpty(ledger);
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.categories.count();
  if (existing > 0) return false;

  const now = nowIso();
  const userId = requireUser(ledger);
  await db.categories.bulkPut(
    DEFAULT_CATEGORIES.map((c, index) => ({
      id: newId(),
      userId,
      name: c.name,
      color: c.color,
      sortOrder: index,
      archivedAt: null,
      rolloverOverride: null,
      isFallback: c.isFallback,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      note: null,
      // The eight defaults are all expense categories, and must say so: blank
      // would classify every one of them as savings and leave a brand-new
      // account with nothing to log against but the fallback.
      savingsOrExpenditure: EXPENDITURE_LABEL,
      keepInAccount: null,
      icon: DEFAULT_ICON_BY_NAME[c.name] ?? null,
      ...DIRTY,
    })),
  );
  queueSync();
  return true;
}

type CategoryPatch = Partial<
  Pick<
    Category,
    "name" | "color" | "sortOrder" | "rolloverOverride" | "note" | "savingsOrExpenditure" | "keepInAccount"
  >
>;

export async function updateCategory(id: string, patch: CategoryPatch, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  await db.transaction("rw", db.categories, () => patchCategoryTx(id, patch, nowIso(), ledger));
  queueSync();
}

/**
 * Patch a category INSIDE the caller's transaction — the single validation
 * chokepoint, so a batched save cannot skip the rules a single edit obeys.
 */
async function patchCategoryTx(id: string, patch: CategoryPatch, now: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.categories.get(id);
  if (!existing) throw new Error(`Category ${id} not found`);
  // The fallback keeps every expense categorised (SPEC.md §4). `isSavingsCategory`
  // already refuses to classify it as savings, so a label that says otherwise
  // would be stored and then silently ignored — a row whose settings disagree
  // with its behaviour. Refuse the write instead (INV-7, C-8).
  if (ledger?.kind === "family" && patch.savingsOrExpenditure !== undefined && isSavingsCategory({ savingsOrExpenditure: patch.savingsOrExpenditure, isFallback: false })) throw new Error("Family categories must be expenditure.");
  if (patch.savingsOrExpenditure !== undefined) {
    // Never store a blank label. The pull-side repair in `fromWireCategory`
    // reads an absent one as "written before v6", so a v6 client writing one
    // would put two meanings back into a single value and hand a genuinely
    // savings category to the next device as expenditure. Every caller today
    // passes a canonical label; this is the chokepoint that keeps that true.
    if ((patch.savingsOrExpenditure ?? "").trim() === "") {
      throw new Error(
        `A category must say whether it is savings or expenditure. Pass "${SAVINGS_LABEL}" or ` +
          `"${EXPENDITURE_LABEL}" rather than a blank label.`,
      );
    }
    if (
      existing.isFallback &&
      isSavingsCategory({ savingsOrExpenditure: patch.savingsOrExpenditure, isFallback: false })
    ) {
      throw new Error(
        `${existing.name} is the fallback category and must stay expenditure — it is what keeps ` +
          `every expense categorised. Mark a different category as savings, or make another ` +
          `category the fallback first.`,
      );
    }
  }
  await db.categories.put({ ...existing, ...patch, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY });
}

/**
 * Retire a category. Archive, never delete — a deleted category would orphan
 * every expense that referenced it (SPEC.md §4).
 */
export async function archiveCategory(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.categories.get(id);
  if (!existing) throw new Error(`Category ${id} not found`);
  if (existing.isFallback) {
    throw new Error("The fallback category cannot be archived — it keeps every expense categorised.");
  }
  const now = nowIso();
  await db.transaction("rw", db.categories, db.profile, async () => {
    await db.categories.put({ ...existing, archivedAt: now, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY });
    await clearDefaultIfAmongTx([id], now, ledger);
  });
  queueSync();
}

export async function unarchiveCategory(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.categories.get(id);
  if (!existing) throw new Error(`Category ${id} not found`);
  await db.categories.put({ ...existing, archivedAt: null, updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

export async function reorderCategories(idsInOrder: readonly string[], ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const now = nowIso();
  await db.transaction("rw", db.categories, async () => {
    for (const [index, id] of idsInOrder.entries()) {
      const existing = await db.categories.get(id);
      if (!existing) continue;
      await db.categories.put({ ...existing, sortOrder: index, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
}

/**
 * The id of the single fallback category (`Other`). Throws if none is live
 * (INV-7): an account with no fallback has nowhere to keep an expense whose
 * category was deleted, which is the orphaning this whole design exists to prevent.
 */
export async function fallbackCategoryId(ledger?: LedgerRef): Promise<string> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const fallback = await db.categories
    .filter((c) => c.isFallback && c.deletedAt === null)
    .first();
  if (!fallback) {
    throw new Error(
      "No fallback category found — every account keeps an “Other” to catch expenses.",
    );
  }
  return fallback.id;
}

/** What to do with a deleted category's live expenses. */
export type ExpenseDisposition = "reassign" | "remove";

export interface DeleteCategoryOptions {
  /**
   * `reassign` moves the category's live expenses to the fallback (`Other`);
   * `remove` tombstones them alongside it. There is no third option, because an
   * expense left pointing at a deleted category renders as "Uncategorised" (INV-7).
   */
  expenses: ExpenseDisposition;
}

export interface DeleteCategoryResult {
  /** How many live expenses were reassigned or tombstoned. */
  affectedExpenses: number;
  /** The prior state, for a ~5-second Undo. */
  undo: UndoSnapshot;
}

/**
 * Tombstone one category and dispose of its live expenses, INSIDE the caller's
 * transaction, appending prior states to `undo`. Every write is a tombstone or a
 * reassignment — never a hard delete (INV-5). Throws on the fallback (INV-7).
 */
async function tombstoneCategoryTx(
  id: string,
  disposition: ExpenseDisposition,
  now: string,
  undo: UndoSnapshot,
  ledger?: LedgerRef,
): Promise<number> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const category = await db.categories.get(id);
  if (!category) throw new Error(`Category ${id} not found`);
  if (category.isFallback) {
    throw new Error(
      "The fallback category cannot be deleted — it keeps every expense categorised.",
    );
  }

  const fallbackId = disposition === "reassign" ? await fallbackCategoryId(ledger) : null;

  const live = await db.expenses
    .where("categoryId")
    .equals(id)
    .filter((e) => e.deletedAt === null)
    .toArray();

  for (const expense of live) {
    if (await isLockedSplitShare(expense, ledger) && fallbackId === null) throw new SplitExpenseLockedError(expense);
    undo.expenses.push(expense);
    const patch = fallbackId !== null ? { categoryId: fallbackId } : { deletedAt: now };
    await db.expenses.put({ ...expense, ...patch, updatedAt: nextUpdatedAt(expense.updatedAt, () => Date.parse(now)), ...DIRTY });
  }

  // Cascade the tombstone to everything the category owns. A budget or rule left
  // behind is exactly the "silent orphan" this contract forbids — it renders
  // nowhere (a budget for a deleted category is never queried) yet syncs forever.
  // 41 such budgets accumulated in production before this cascade existed.
  const liveBudgets = await db.budgets
    .where("categoryId")
    .equals(id)
    .filter((b) => b.deletedAt === null)
    .toArray();
  for (const budget of liveBudgets) {
    undo.budgets.push(budget);
    await db.budgets.put({ ...budget, deletedAt: now, updatedAt: nextUpdatedAt(budget.updatedAt, () => Date.parse(now)), ...DIRTY });
  }

  const liveRules = await db.recurringRules
    .where("categoryId")
    .equals(id)
    .filter((r) => r.deletedAt === null)
    .toArray();
  for (const rule of liveRules) {
    undo.recurringRules.push(rule);
    await db.recurringRules.put({ ...rule, deletedAt: now, updatedAt: nextUpdatedAt(rule.updatedAt, () => Date.parse(now)), ...DIRTY });
  }

  undo.categories.push(category);
  await db.categories.put({ ...category, deletedAt: now, updatedAt: nextUpdatedAt(category.updatedAt, () => Date.parse(now)), ...DIRTY });
  return live.length;
}

/**
 * Delete a category as a tombstone (INV-5), with an explicit decision about its
 * expenses (INV-7) — never a hard delete, never a silent orphan. `reassign` moves
 * them to `Other`; `remove` tombstones them too. Returns the affected count and an
 * undo snapshot.
 */
export async function deleteCategory(
  id: string,
  options: DeleteCategoryOptions,
  ledger?: LedgerRef,
): Promise<DeleteCategoryResult> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  const undo: UndoSnapshot = { categories: [], expenses: [], budgets: [], recurringRules: [] };
  let affectedExpenses = 0;
  await db.transaction("rw", [db.categories, db.expenses, db.budgets, db.recurringRules, db.profile, db.splitGroups, db.splitExpenses, db.meta, db.linkedSplitRows], async () => {
    affectedExpenses = await tombstoneCategoryTx(id, options.expenses, now, undo, ledger);
    await clearDefaultIfAmongTx([id], now, ledger);
  });
  queueSync();
  return { affectedExpenses, undo };
}

/**
 * Delete several categories at once — atomic in one transaction, one queued sync
 * (the `reorderCategories` precedent). Either they all tombstone or none do, so a
 * failure part-way cannot leave a half-deleted set.
 */
export async function deleteCategories(
  ids: readonly string[],
  options: DeleteCategoryOptions,
  ledger?: LedgerRef,
): Promise<DeleteCategoryResult> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  const undo: UndoSnapshot = { categories: [], expenses: [], budgets: [], recurringRules: [] };
  let affectedExpenses = 0;
  await db.transaction("rw", [db.categories, db.expenses, db.budgets, db.recurringRules, db.profile, db.splitGroups, db.splitExpenses, db.meta, db.linkedSplitRows], async () => {
    for (const id of ids) {
      affectedExpenses += await tombstoneCategoryTx(id, options.expenses, now, undo, ledger);
    }
    await clearDefaultIfAmongTx(ids, now, ledger);
  });
  queueSync();
  return { affectedExpenses, undo };
}

/** Archive several categories at once. Throws on the fallback (INV-7); atomic. */
export async function archiveCategories(ids: readonly string[], ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.categories, db.profile, async () => {
    for (const id of ids) {
      const category = await db.categories.get(id);
      if (!category) throw new Error(`Category ${id} not found`);
      if (category.isFallback) {
        throw new Error(
          "The fallback category cannot be archived — it keeps every expense categorised.",
        );
      }
      await db.categories.put({ ...category, archivedAt: now, updatedAt: nextUpdatedAt(category.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
    await clearDefaultIfAmongTx(ids, now, ledger);
  });
  queueSync();
}

/** Restore several archived categories at once. */
export async function unarchiveCategories(ids: readonly string[], ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.categories, async () => {
    for (const id of ids) {
      const category = await db.categories.get(id);
      if (!category) throw new Error(`Category ${id} not found`);
      await db.categories.put({ ...category, archivedAt: null, updatedAt: nextUpdatedAt(category.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
}

/** Recolour several categories to one curated swatch at once. */
export async function recolorCategories(
  ids: readonly string[],
  color: string,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.categories, async () => {
    for (const id of ids) {
      const category = await db.categories.get(id);
      if (!category) throw new Error(`Category ${id} not found`);
      await db.categories.put({ ...category, color, updatedAt: nextUpdatedAt(category.updatedAt, () => Date.parse(now)), ...DIRTY });
    }
  });
  queueSync();
}

/* ── budgets ───────────────────────────────────────────────────────────────── */

/**
 * Write (or replace) the budget version at `effectiveFrom`, INSIDE the caller's
 * transaction. The single versioning chokepoint (DATA_MODEL.md §2.3): setting a
 * budget twice on the same date replaces that version rather than stacking
 * another, so `setBudget` and `setBudgets` cannot drift apart.
 */
async function putBudgetTx(
  userId: string,
  categoryId: string,
  amountCents: Cents | null,
  effectiveFrom: string,
  now: string,
  ledger?: LedgerRef,
): Promise<Budget> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.budgets
    .where("categoryId")
    .equals(categoryId)
    .filter((b) => b.effectiveFrom === effectiveFrom && b.deletedAt === null)
    .first();

  const row = {
    id: existing?.id ?? newId(),
    userId,
    categoryId,
    amountCents,
    effectiveFrom,
    createdAt: existing?.createdAt ?? now,
    updatedAt: nextUpdatedAt(existing?.updatedAt, () => Date.parse(now)),
    deletedAt: null,
    ...DIRTY,
  };
  await db.budgets.put(row);
  return row;
}

/**
 * Set a category's budget from `effectiveFrom` onward.
 *
 * Versioned, not mutated (DATA_MODEL.md §2.3): raising a budget in July must not
 * retroactively make March compliant.
 */
export async function setBudget(
  categoryId: string,
  amountCents: Cents | null,
  effectiveFrom: string,
  ledger?: LedgerRef,
): Promise<Budget> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const userId = requireUser(ledger);
  const now = nowIso();
  const row = await db.transaction("rw", db.budgets, () =>
    putBudgetTx(userId, categoryId, amountCents, effectiveFrom, now, ledger),
  );
  if (ledger?.kind !== "family") notificationNeed();
  queueSync();
  return row;
}

/**
 * Set the same budget on several categories at once, effective from the given
 * date — atomic, one queued sync. Reuses the single-category chokepoint so the
 * versioning rules cannot diverge between the two.
 */
export async function setBudgets(
  ids: readonly string[],
  amountCents: Cents | null,
  effectiveFrom: string,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const userId = requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.budgets, async () => {
    for (const id of ids) {
      await putBudgetTx(userId, id, amountCents, effectiveFrom, now, ledger);
    }
  });
  if (ledger?.kind !== "family") notificationNeed();
  queueSync();
}

/* ── salary ────────────────────────────────────────────────────────────────── */

/**
 * Set the monthly salary from `effectiveFrom` onward.
 *
 * Versioned exactly as a budget is, and for the same reason: a raise in July
 * must not retroactively change what June's expense ceiling was. One row per
 * `effectiveFrom`, replaced in place if it already exists, so editing the figure
 * twice in one period does not accumulate versions nobody asked for.
 */
export async function setSalary(amountCents: Cents, effectiveFrom: string, ledger?: LedgerRef): Promise<Salary> {
  if (ledger?.kind === "family") throw new Error("Family ledgers have no salary.");
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const userId = requireUser(ledger);
  const now = nowIso();
  const row = await db.transaction("rw", db.salaries, () =>
    putSalaryTx(userId, amountCents, effectiveFrom, now, ledger),
  );
  queueSync();
  return row;
}

/** The salary versioning chokepoint, INSIDE the caller's transaction. */
async function putSalaryTx(
  userId: string,
  amountCents: Cents,
  effectiveFrom: string,
  now: string,
  ledger?: LedgerRef,
): Promise<Salary> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.salaries
    .where("effectiveFrom")
    .equals(effectiveFrom)
    .filter((s) => s.deletedAt === null)
    .first();

  const next = {
    id: existing?.id ?? newId(),
    userId,
    amountCents,
    effectiveFrom,
    createdAt: existing?.createdAt ?? now,
    updatedAt: nextUpdatedAt(existing?.updatedAt, () => Date.parse(now)),
    deletedAt: null,
    ...DIRTY,
  };
  await db.salaries.put(next);
  return next;
}

export async function clearBudget(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.budgets.get(id);
  if (!existing) throw new Error(`Budget ${id} not found`);
  await db.budgets.put({ ...existing, deletedAt: nowIso(), updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

/** Clear from this date onward with a live null version, atomic and one queued sync. */
export async function clearCategoryBudgets(ids: readonly string[], periodStart: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const userId = requireUser(ledger);
  const now = nowIso();
  await db.transaction("rw", db.budgets, async () => {
    for (const id of ids) await clearBudgetTx(userId, id, periodStart, now, ledger);
  });
  queueSync();
}

/** A clear is a version; tombstones remain reserved for deletions (INV-5). */
async function clearBudgetTx(userId: string, categoryId: string, periodStart: string, now: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const rows = await db.budgets.where("categoryId").equals(categoryId)
    .filter(row => row.userId === userId && row.deletedAt === null).toArray();
  const date = parseYmd(periodStart);
  if (!date) throw new Error(`Invalid budget clear date: ${periodStart}`);
  if (versionAt(rows, periodStart)?.amountCents == null) return;
  await putBudgetTx(userId, categoryId, null, periodStart, now, ledger);
}

/* ── recurring rules ───────────────────────────────────────────────────────── */

export async function addRecurringRule(input: {
  categoryId: string;
  amountCents: Cents;
  dayOfMonth: number;
  note?: string | null;
}, ledger?: LedgerRef): Promise<RecurringRule> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const now = nowIso();
  const row = {
    id: newId(),
    userId: requireUser(ledger),
    categoryId: input.categoryId,
    amountCents: input.amountCents,
    dayOfMonth: input.dayOfMonth,
    note: input.note ?? null,
    active: true,
    lastMaterialisedOn: null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    ...DIRTY,
  };
  await db.recurringRules.put(row);
  if (ledger?.kind !== "family") notificationNeed();
  queueSync();
  return row;
}

export async function updateRecurringRule(
  id: string,
  patch: Partial<
    Pick<RecurringRule, "categoryId" | "amountCents" | "dayOfMonth" | "note" | "active" | "lastMaterialisedOn">
  >,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.recurringRules.get(id);
  if (!existing) throw new Error(`Recurring rule ${id} not found`);
  await db.recurringRules.put({ ...existing, ...patch, updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

export async function deleteRecurringRule(id: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.recurringRules.get(id);
  if (!existing) throw new Error(`Recurring rule ${id} not found`);
  await db.recurringRules.put({ ...existing, deletedAt: nowIso(), updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

/* ── profile ───────────────────────────────────────────────────────────────── */

type ProfilePatch = Partial<
  Pick<Profile, "currency" | "timezone" | "cycleStartDay" | "rolloverMode" | "onboardedAt" | "defaultCategoryId" | "gridOrder">
>;

export async function updateProfile(patch: ProfilePatch, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const id = requireUser(ledger);
  await db.transaction("rw", db.profile, () => putProfileTx(id, patch, nowIso(), ledger));
  queueSync();
}

/** Automatic cleanup must never synthesize a personal profile. */
export async function clearGridOrderIfSet(): Promise<void> {
  const id = getCurrentUser();
  if (!id) return;
  const changed = await personalDb.transaction("rw", personalDb.profile, async () => {
    const profile = await personalDb.profile.get(id);
    if (profile?.gridOrder == null) return false;
    await putProfileTx(id, { gridOrder: null }, nowIso());
    return true;
  });
  if (changed) queueSync();
}

/** Patch the single profile row INSIDE the caller's transaction, defaults and all. */
async function putProfileTx(id: string, patch: ProfilePatch, now: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const existing = await db.profile.get(id);
  const base = existing ?? defaultProfile(id, now);
  await db.profile.put({ ...base, ...patch, updatedAt: nextUpdatedAt(existing?.updatedAt, () => Date.parse(now)), ...DIRTY });
}

/* ── the Budget screen's batched save ──────────────────────────────────────── */

/**
 * One change the Budget screen wants written. Mirrors `SettingsChange` in
 * `features/settings/budget_draft.ts`, which is where they are derived; the
 * shape is restated rather than imported so `lib/` does not depend on a feature
 * (W-1).
 */
export type BudgetSettingsChange =
  | { readonly kind: "profile"; readonly patch: ProfilePatch }
  | { readonly kind: "salary"; readonly amountCents: Cents }
  | { readonly kind: "budget"; readonly categoryId: string; readonly amountCents: Cents }
  | { readonly kind: "clearBudget"; readonly categoryId: string }
  | { readonly kind: "categoryKind"; readonly categoryId: string; readonly isSavings: boolean }
  | {
      readonly kind: "rolloverOverride";
      readonly categoryId: string;
      readonly rolloverOverride: Category["rolloverOverride"];
    };

/**
 * Commit a whole Settings draft — **one transaction, one queued sync.**
 *
 * This is what "Settings only change and sync on save" means in the write layer,
 * and both halves of it matter:
 *
 * - **Atomic.** Every figure on Horizon is derived from several of these rows at
 *   once. A save that wrote the salary and then failed on a budget would leave a
 *   ceiling computed from numbers the user never saw together — and, worse, one
 *   they would have no reason to distrust. A rejection here rolls back to
 *   exactly the state the screen is still showing (INV-7).
 * - **One sync.** Every per-field write used to call `queueSync()` on its own.
 *
 * `effectiveFrom` is passed in, never derived here: the caller recomputes the
 * period start from the draft's **new** `cycleStartDay` and `timezone`, so a
 * save that changes the cut-off day *and* an amount stamps the amount at the
 * boundary it will actually be read against. No period is stored (INV-2).
 *
 * Nothing awaits the network — this is a Dexie write like any other (INV-4).
 */
export async function applyBudgetSettings(
  changes: readonly BudgetSettingsChange[],
  effectiveFrom: string,
  ledger?: LedgerRef,
): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  if (ledger?.kind === "family" && changes.some(change => change.kind === "salary")) throw new Error("Family ledgers have no salary.");
  if (changes.length === 0) return;
  const id = requireUser(ledger);
  const now = nowIso();

  await db.transaction(
    "rw",
    db.profile,
    db.salaries,
    db.budgets,
    db.categories,
    async () => {
      // A fresh account has no profile row yet and runs on the defaults; its
      // boundary still moves when the first save sets a cut-off day.
      const oldProfile = (await db.profile.get(id)) ?? defaultProfile(id, now);
      // Profile first, unconditionally. The caller derived `effectiveFrom` from
      // these values, so applying them after a version had already been stamped
      // would leave the two disagreeing about where the period starts.
      for (const change of changes) {
        if (change.kind === "profile") await putProfileTx(id, change.patch, now, ledger);
      }

      const newProfile = await db.profile.get(id);
      if (newProfile && oldProfile.cycleStartDay !== newProfile.cycleStartDay) {
        const oldPeriod = periodBounds(new Date(now), oldProfile.cycleStartDay, oldProfile.timezone);
        const date = parseYmd(effectiveFrom);
        if (date === null) throw new Error(`Invalid budget effective date: ${effectiveFrom}. Expected YYYY-MM-DD.`);
        const newPeriod = periodBounds(
          zonedMidnight(date, newProfile.timezone),
          newProfile.cycleStartDay,
          newProfile.timezone,
        );
        const oldStart = periodStartDate(oldPeriod, oldProfile.timezone);
        if (effectiveFrom < oldStart) {
          const salaries = await db.salaries.filter((row) => row.userId === id && row.deletedAt === null).toArray();
          if (!changes.some((change) => change.kind === "salary")) {
            const amount = carryAcrossBoundary(salaries, oldPeriod, newPeriod, oldProfile.timezone, newProfile.timezone, salaryInForce);
            if (amount !== null && salaryInForce(salaries, newPeriod, newProfile.timezone) !== amount) {
              await putSalaryTx(id, amount, effectiveFrom, now, ledger);
            }
          }
          const editedBudgets = new Set(changes.flatMap((change) =>
            change.kind === "budget" || change.kind === "clearBudget" ? [change.categoryId] : [],
          ));
          const budgets = await db.budgets.filter((row) => row.userId === id && row.deletedAt === null).toArray();
          const byCategory = new Map<string, LocalBudget[]>();
          for (const row of budgets) {
            const versions = byCategory.get(row.categoryId) ?? [];
            versions.push(row);
            byCategory.set(row.categoryId, versions);
          }
          for (const [categoryId, versions] of byCategory) {
            if (editedBudgets.has(categoryId)) continue;
            const selected = carriedBoundaryVersion(versions, oldPeriod, newPeriod, oldProfile.timezone, newProfile.timezone);
            if (selected !== null && selectedBoundaryVersion(versions, newPeriod, newProfile.timezone) !== selected) {
              await putBudgetTx(id, categoryId, selected.amountCents, effectiveFrom, now, ledger);
            }
          }
          // INV-5: retire every skipped version, including explicitly edited or cleared amounts.
          for (const row of redundantBoundaryVersions(salaries, effectiveFrom, oldStart)) {
            await db.salaries.put({ ...row, deletedAt: now, updatedAt: nextUpdatedAt(row.updatedAt, () => Date.parse(now)), ...DIRTY });
          }
          for (const row of redundantBoundaryVersions(budgets, effectiveFrom, oldStart)) {
            await db.budgets.put({ ...row, deletedAt: now, updatedAt: nextUpdatedAt(row.updatedAt, () => Date.parse(now)), ...DIRTY });
          }
        }
      }

      for (const change of changes) {
        switch (change.kind) {
          case "profile":
            break;
          case "salary":
            await putSalaryTx(id, change.amountCents, effectiveFrom, now, ledger);
            break;
          case "budget":
            await putBudgetTx(id, change.categoryId, change.amountCents, effectiveFrom, now, ledger);
            break;
          case "clearBudget":
            await clearBudgetTx(id, change.categoryId, effectiveFrom, now, ledger);
            break;
          case "categoryKind":
            await patchCategoryTx(
              change.categoryId,
              { savingsOrExpenditure: change.isSavings ? SAVINGS_LABEL : EXPENDITURE_LABEL },
              now,
              ledger,
            );
            break;
          case "rolloverOverride":
            await patchCategoryTx(
              change.categoryId,
              { rolloverOverride: change.rolloverOverride },
              now,
              ledger,
            );
            break;
        }
      }
    },
  );

  if (ledger?.kind !== "family" && changes.some((change) => change.kind === "budget")) notificationNeed();
  queueSync();
}

/* ── icons and the default category ────────────────────────────────────────── */

/**
 * Set a category's icon: exactly one emoji, or null for none.
 *
 * Checked here, at the way in, and loudly (INV-7) — never trimmed to its first
 * glyph. The read boundary accepts any string, because a row from another
 * device is not this client's to reject over a cosmetic field.
 */
export async function setCategoryIcon(id: string, icon: string | null, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  if (icon !== null && !isSingleEmoji(icon)) {
    throw new Error(`A category icon must be one emoji, received ${JSON.stringify(icon)}.`);
  }
  const existing = await db.categories.get(id);
  if (!existing) throw new Error(`Category ${id} not found`);
  await db.categories.put({ ...existing, icon, updatedAt: nextUpdatedAt(existing.updatedAt), ...DIRTY });
  queueSync();
}

/**
 * Choose the category Capture opens on, or null to open on the last one used.
 * Refuses a category that is archived or deleted: a default that cannot be
 * selected would leave Capture opening on nothing.
 */
export async function setDefaultCategory(id: string | null, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  if (id !== null) {
    const category = await db.categories.get(id) ?? (ledger ? undefined : await familyDb.categories.get(id));
    if (!category) throw new Error(`Category ${id} not found`);
    if (category.deletedAt !== null || category.archivedAt !== null) {
      throw new Error("An archived or deleted category cannot be the default.");
    }
    if (isSavingsCategory(category)) throw new Error("A savings category cannot be the default. Pick an expenditure category.");
  }
  await updateProfile({ defaultCategoryId: id }, ledger);
}

/**
 * Inside a write that retires categories: if one of them is the default,
 * clear it in the same transaction, so there is no moment — and no synced
 * state — in which the profile points at a category Capture cannot select.
 */
async function clearDefaultIfAmongTx(ids: readonly string[], now: string, ledger?: LedgerRef): Promise<void> {
  assertLedgerWritable(ledger);
  const db = ledger?.db ?? personalDb;
  const profile = await db.profile.get(requireUser(ledger));
  if (!profile?.defaultCategoryId || !ids.includes(profile.defaultCategoryId)) return;
  await db.profile.put({ ...profile, defaultCategoryId: null, updatedAt: nextUpdatedAt(profile.updatedAt, () => Date.parse(now)), ...DIRTY });
}

export const FALLBACK_CATEGORY = DEFAULT_CATEGORIES.find(category => category.isFallback)!;
const RECURRING_MIDDAY_OFFSET_MS = 12 * 60 * 60 * 1000;
let warnedInvalidTimestamp = false;

/** Inject the clock in tests without changing IndexedDB's timer scheduling. */
export function nextUpdatedAt(previous?: string, clock: () => number = Date.now): string {
  const parsed = previous === undefined ? -Infinity : Date.parse(previous);
  if (Number.isNaN(parsed) && !warnedInvalidTimestamp) {
    warnedInvalidTimestamp = true;
    console.warn("Invalid previous update timestamp; using the current clock.");
  }
  const next = new Date(Math.max(clock(), Number.isNaN(parsed) ? -Infinity : parsed + 1));
  if (Number.isNaN(next.getTime())) throw new Error(`The last change time ${String(previous)} cannot be advanced; this row's timestamp is out of range.`);
  return next.toISOString();
}

/** Seed only after the family pull has settled; a stable id deduplicates members. */
export async function seedFamilyFallbackIfEmpty(ledger: LedgerRef): Promise<boolean> {
  if (ledger.kind !== "family") throw new Error("Expected a family ledger.");
  assertLedgerWritable(ledger);
  const store = ledger.db;
  return store.transaction("rw", store.categories, async () => {
    if (await store.categories.filter(c => c.isFallback && c.deletedAt === null).count()) return false;
    const id = `family-other:${ledger.ownerId}`;
    const previous = await store.categories.get(id);
    const now = nextUpdatedAt(previous?.updatedAt);
    await store.categories.put({ id, userId: ledger.ownerId,
      name: FALLBACK_CATEGORY.name, color: FALLBACK_CATEGORY.color, sortOrder: 0, archivedAt: null, rolloverOverride: null,
      isFallback: true, createdAt: now, updatedAt: now, deletedAt: null, note: null,
      savingsOrExpenditure: EXPENDITURE_LABEL, keepInAccount: null, icon: "•••", ...DIRTY });
    queueSync();
    return true;
  });
}

/** The server accepts any nonempty row id; delimiters encode an unambiguous pair. */
export function recurringConfirmationId(ruleId: string, dueOn: string): string {
  return `recurring:${encodeURIComponent(ruleId)}:${dueOn}`;
}

/** One atomic local confirmation; the network stays on the background queue (INV-4). */
export async function confirmRecurring(ruleId: string, dueOn: string, ledger?: LedgerRef): Promise<Expense | null> {
  const store = ledger?.db ?? personalDb;
  const ownerId = requireUser(ledger);
  const date = parseYmd(dueOn);
  if (!date) throw new Error("Expected a YYYY-MM-DD recurring due date.");
  const id = recurringConfirmationId(ruleId, dueOn);
  const result = await store.transaction("rw", store.expenses, store.recurringRules, store.categories, store.profile, async () => {
    const rule = await store.recurringRules.get(ruleId);
    if (!rule || rule.userId !== ownerId || rule.deletedAt !== null) throw new Error("Recurring rule not found in this ledger.");
    const existing = await store.expenses.get(id);
    if (existing) {
      if (existing.userId !== ownerId) throw new Error("Recurring confirmation belongs to another ledger.");
      if (!rule.lastMaterialisedOn || rule.lastMaterialisedOn < dueOn) {
        await store.recurringRules.put({ ...rule, lastMaterialisedOn: dueOn, updatedAt: nextUpdatedAt(rule.updatedAt), ...DIRTY });
      }
      return existing.deletedAt === null ? existing : null;
    }
    const category = await store.categories.get(rule.categoryId);
    if (category && isSavingsCategory(category)) throw new Error("Recurring expenses require an expenditure category.");
    const profile = await store.profile.get(ownerId);
    const occurredAt = new Date(zonedMidnight(date, profile?.timezone ?? defaultProfile(ownerId, "").timezone).getTime() + RECURRING_MIDDAY_OFFSET_MS).toISOString();
    const now = nowIso();
    const row: LocalExpense = { id, userId: ownerId, categoryId: rule.categoryId,
      amountCents: rule.amountCents, occurredAt, note: rule.note, recurringRuleId: ruleId,
      sourceId: null, createdAt: now, updatedAt: now, deletedAt: null, ...DIRTY };
    await store.expenses.put(row);
    await store.recurringRules.put({ ...rule, lastMaterialisedOn: rule.lastMaterialisedOn && rule.lastMaterialisedOn > dueOn ? rule.lastMaterialisedOn : dueOn,
      updatedAt: nextUpdatedAt(rule.updatedAt), ...DIRTY });
    return row;
  });
  queueSync();
  return result;
}

/** Personal mixed preference; shared category sortOrder belongs to the family. */
export async function reorderCaptureCategories(ids: readonly string[], member: boolean): Promise<void> {
  if (member) await familyDb.categories.toArray();
  const now = nowIso();
  await personalDb.transaction("rw", personalDb.categories, personalDb.profile, async () => {
    const personal = await personalDb.categories.toArray();
    const personalIds = new Set(personal.map(row => row.id));
    const order = ids.filter(id => personalIds.has(id));
    for (const [index, id] of order.entries()) {
      const existing = personal.find(row => row.id === id)!;
      await personalDb.categories.put({
        ...existing, sortOrder: index, updatedAt: nextUpdatedAt(existing.updatedAt, () => Date.parse(now)), ...DIRTY,
      });
    }
    if (member) await putProfileTx(requireUser(), { gridOrder: [...ids] }, now);
  });
  queueSync();
}


export function getCurrentUser(): string | null { return userId; }

/* ── owned personal splits ────────────────────────────────────────────────── */

export class SplitExpenseLockedError extends Error {
  readonly code = "edit_split_instead";
  readonly expense: Pick<Expense, "id" | "note" | "splitRef">;
  constructor(expense: Pick<Expense, "id" | "note" | "splitRef">) { super(`${expense.splitRef?.linkId ? "Update from split" : "Edit the split"} for ${expense.note?.trim() ? `“${expense.note.trim()}”` : "a split share"} instead.`); this.expense = expense; this.name = "SplitExpenseLockedError"; }
}

/** Live owned sources and explicit linked imports lock personal share amounts. */
export async function isLockedSplitShare(expense: Expense, ledger?: LedgerRef): Promise<boolean> {
  if (!expense.splitRef || ledger?.kind === "family") return false;
  if (expense.splitRef.linkId) {
    if (expense.userId !== requireUser()) return false;
    const link = (await readLinkedList()).find(row => row.link_id === expense.splitRef!.linkId);
    if (!link || link.ended) return false;
    const group = await personalDb.linkedSplitRows.get([link.link_id, "split_groups", expense.splitRef.groupId]);
    return !!group && !group.deleted;
  }
  const db = ledger?.db ?? personalDb;
  const owner = requireUser();
  const split = await db.splitExpenses.get(expense.splitRef.splitExpenseId);
  const group = await db.splitGroups.get(expense.splitRef.groupId);
  if (!split || !group) return false;
  return expense.userId === owner && split.userId === owner && split.deletedAt === null
    && split.groupId === group.id && group.userId === owner && group.deletedAt === null;
}

export function splitShareId(splitExpenseId: string): string { return `${splitExpenseId}:share`; }

function assertShareAmount(amount: Cents, currency: string): void {
  if (amount > MAX_AMOUNT_CENTS) throw new SplitRowError(`Your share must be at most ${formatMoney(cents(MAX_AMOUNT_CENTS), currency)}. Reduce the split amount or your share.`);
}

function personalSplitStore(ledger?: LedgerRef) {
  const owner = requireUser();
  if (ledger && (ledger.kind !== "personal" || ledger.db === familyDb || ledger.ownerId !== owner)) throw new Error("Splits belong to the signed-in owner's personal ledger.");
  return { db: ledger?.db ?? personalDb, owner };
}

async function ownedSplitGroup(id: string, ledger?: LedgerRef, allowDeleted = false): Promise<LocalSplitGroup> {
  const { db, owner } = personalSplitStore(ledger);
  const group = await db.splitGroups.get(id);
  if (!group || (!allowDeleted && group.deletedAt !== null) || group.userId !== owner) throw new Error("Split group is unavailable or belongs to another owner.");
  return group;
}

function splitStamp(previous?: { updatedAt: string }) { return nextUpdatedAt(previous?.updatedAt); }
function splitTombstone<T extends { updatedAt: string; deletedAt: string | null }>(row: T): T & SyncMeta {
  const updatedAt = splitStamp(row);
  return { ...row, updatedAt, deletedAt: updatedAt, ...DIRTY };
}

/** New rows undo to tombstones; existing rows undo to their complete prior snapshots. */
export interface SplitUndoSnapshot {
  groups: LocalSplitGroup[];
  splitExpenses: LocalSplitExpense[];
  transfers: LocalSplitTransfer[];
  expenses: LocalExpense[];
}
function splitUndo(): SplitUndoSnapshot { return { groups: [], splitExpenses: [], transfers: [], expenses: [] }; }

export async function restoreSplitRows(snapshot: SplitUndoSnapshot, ledger?: LedgerRef): Promise<void> {
  const { db, owner } = personalSplitStore(ledger);
  await db.transaction("rw", [db.splitGroups, db.splitExpenses, db.splitTransfers, db.expenses], async () => {
    async function restore<T extends { id: string; userId: string; updatedAt: string }>(table: Table<T, string>, rows: readonly T[]) {
      for (const row of rows) {
        const current = await table.get(row.id);
        if (row.userId !== owner || (current && current.userId !== owner)) throw new Error("Cannot restore another owner's split rows.");
        await table.put({ ...row, updatedAt: nextUpdatedAt(current?.updatedAt ?? row.updatedAt), ...DIRTY });
      }
    }
    await restore(db.splitGroups, snapshot.groups);
    await restore(db.splitExpenses, snapshot.splitExpenses);
    await restore(db.splitTransfers, snapshot.transfers);
    await restore(db.expenses, snapshot.expenses);
  });
  queueSync();
}

export async function createSplitGroup(input: { name: string; baseCurrency: string; members: readonly string[]; meName?: string }, ledger?: LedgerRef): Promise<LocalSplitGroup> {
  const { db, owner } = personalSplitStore(ledger);
  const names = input.members.map(name => name.trim());
  const meName = input.meName?.trim() ?? names[0];
  if (!meName) throw new Error("Name your own member before creating a group.");
  if (!names.includes(meName)) names.unshift(meName);
  const members = names.map(name => ({ id: newId(), name, archivedAt: null }));
  const now = nowIso();
  const row: LocalSplitGroup = { ...fromWireSplitGroup({ id: newId(), userId: owner, name: input.name.trim(), baseCurrency: input.baseCurrency,
    members, meMemberId: members[names.indexOf(meName)]!.id, rateHints: {}, homeRateNano: null, archivedAt: null, createdAt: now, updatedAt: now, deletedAt: null }), ...DIRTY };
  await db.splitGroups.put(row);
  queueSync();
  return row;
}

export interface SplitGroupPatch {
  name?: string;
  baseCurrency?: string;
  addMembers?: readonly string[];
  renameMembers?: readonly { id: string; name: string }[];
  archiveMemberIds?: readonly string[];
  removeMemberIds?: readonly string[];
}

export async function updateSplitGroup(id: string, patch: SplitGroupPatch, ledger?: LedgerRef): Promise<{ group: LocalSplitGroup; undo: SplitUndoSnapshot }> {
  const { db } = personalSplitStore(ledger);
  const undo = splitUndo();
  const group = await db.transaction("rw", [db.splitGroups, db.splitExpenses, db.splitTransfers], async () => {
    const previous = await ownedSplitGroup(id, ledger);
    const expenses = await db.splitExpenses.where("groupId").equals(id).toArray();
    const transfers = await db.splitTransfers.where("groupId").equals(id).toArray();
    const changedCurrency = patch.baseCurrency !== undefined && patch.baseCurrency !== previous.baseCurrency;
    if (changedCurrency && (expenses.length > 0 || transfers.length > 0)) throw new Error("Base currency can change only before the group has expenses or transfers.");
    const { balances, skipped } = computeBalances(previous, expenses.filter(e => e.deletedAt === null), transfers.filter(t => t.deletedAt === null));
    const members = previous.members.map(member => ({ ...member }));
    const findMember = (memberId: string) => {
      const member = members.find(m => m.id === memberId);
      if (!member) throw new Error(`Member ${memberId} is not in this group.`);
      return member;
    };
    for (const rename of patch.renameMembers ?? []) findMember(rename.id).name = rename.name.trim();
    for (const memberId of patch.archiveMemberIds ?? []) {
      if (memberId === previous.meMemberId) throw new Error("The owner's member must stay active.");
      findMember(memberId).archivedAt = nowIso();
    }
    for (const memberId of patch.removeMemberIds ?? []) {
      findMember(memberId);
      const referenced = expenses.some(e => e.payerMemberId === memberId || e.entries.some(entry => entry.memberId === memberId) || e.items?.some(item => item.memberIds.includes(memberId)))
        || transfers.some(t => t.from === memberId || t.to === memberId);
      if (memberId === previous.meMemberId || referenced || balances.get(memberId) !== 0 || skipped.length > 0) throw new Error("Archive this member instead; their entries or balance must remain readable.");
      members.splice(members.findIndex(m => m.id === memberId), 1);
    }
    members.push(...(patch.addMembers ?? []).map(name => ({ id: newId(), name: name.trim(), archivedAt: null })));
    const row: LocalSplitGroup = { ...previous, ...fromWireSplitGroup({ ...previous, name: patch.name?.trim() ?? previous.name,
      baseCurrency: patch.baseCurrency ?? previous.baseCurrency, members, ...(changedCurrency ? { rateHints: {}, homeRateNano: null } : {}) }), updatedAt: splitStamp(previous), ...DIRTY };
    undo.groups.push(previous);
    await db.splitGroups.put(row);
    return row;
  });
  queueSync();
  return { group, undo };
}

export async function archiveSplitGroup(id: string, ledger?: LedgerRef): Promise<SplitUndoSnapshot> {
  const { db } = personalSplitStore(ledger);
  const undo = splitUndo();
  await db.transaction("rw", db.splitGroups, async () => {
    const group = await ownedSplitGroup(id, ledger);
    undo.groups.push(group);
    const updatedAt = splitStamp(group);
    await db.splitGroups.put({ ...group, archivedAt: updatedAt, updatedAt, ...DIRTY });
  });
  queueSync();
  return undo;
}

export async function deleteSplitGroup(id: string, ledger?: LedgerRef): Promise<SplitUndoSnapshot> {
  const { db, owner } = personalSplitStore(ledger);
  const undo = splitUndo();
  await db.transaction("rw", [db.splitGroups, db.splitExpenses, db.splitTransfers, db.expenses], async () => {
    const group = await ownedSplitGroup(id, ledger, true);
    undo.groups.push(group);
    undo.splitExpenses = await db.splitExpenses.where("groupId").equals(id).filter(e => e.deletedAt === null).toArray();
    undo.transfers = await db.splitTransfers.where("groupId").equals(id).filter(t => t.deletedAt === null).toArray();
    const splitIds = await db.splitExpenses.where("groupId").equals(id).primaryKeys();
    undo.expenses = await db.expenses.where("splitRef.splitExpenseId").anyOf(splitIds).filter(e => e.splitRef?.groupId === id && e.deletedAt === null).toArray();
    for (const row of [...undo.splitExpenses, ...undo.transfers, ...undo.expenses]) if (row.userId !== owner) throw new Error("Cannot delete another owner's split rows.");
    await db.splitGroups.put(splitTombstone(group));
    for (const row of undo.splitExpenses) await db.splitExpenses.put(splitTombstone(row));
    for (const row of undo.transfers) await db.splitTransfers.put(splitTombstone(row));
    for (const row of undo.expenses) await db.expenses.put(splitTombstone(row));
  });
  queueSync();
  return undo;
}

export type NewSplitExpense = Omit<SplitExpense, "id" | "userId" | "createdAt" | "updatedAt" | "deletedAt" | "homeRateNano"> & { id?: string; homeRateNano?: RateNano | null };

function splitGroupKey(group: LocalSplitGroup): string {
  return `${group.meMemberId}|${group.baseCurrency}|${group.members.map(member => member.id).sort().join(",")}`;
}

export async function saveSplitExpense(input: NewSplitExpense, options: { categoryId: string; homeRateNano?: RateNano | null }, ledger?: LedgerRef): Promise<{ expense: LocalSplitExpense; shareExpense: LocalExpense | null; undo: SplitUndoSnapshot }> {
  const { db, owner } = personalSplitStore(ledger);
  const undo = splitUndo();
  const result = await db.transaction("rw", [db.splitGroups, db.splitExpenses, db.expenses, db.categories, db.profile], async () => {
    const profile = await db.profile.get(owner);
    const homeCurrency = profile?.currency ?? defaultProfile(owner, nowIso()).currency;
    const group = await ownedSplitGroup(input.groupId, ledger);
    const previous = input.id ? await db.splitExpenses.get(input.id) : undefined;
    if (input.id && (!previous || previous.userId !== owner || previous.groupId !== group.id || previous.deletedAt !== null)) throw new Error("Split expense is unavailable or belongs to another owner/group.");
    const homeRate = options.homeRateNano === undefined ? previous ? previous.homeRateNano : group.homeRateNano : options.homeRateNano;
    const updatedAt = splitStamp(previous);
    const expense: LocalSplitExpense = { ...previous, ...fromWireSplitExpense({ ...input, homeRateNano: group.baseCurrency === homeCurrency || input.currency === homeCurrency ? null : homeRate, id: previous?.id ?? newId(), userId: owner,
      createdAt: previous?.createdAt ?? updatedAt, updatedAt, deletedAt: null }), ...DIRTY };
    const memberIds = group.members.map(m => m.id);
    const validation = validateSplit({ ...expense, memberIds });
    if (!validation.ok) throw new Error(`${validation.reason}: ${validation.detail}`);
    if (!memberIds.includes(expense.payerMemberId) || !memberIds.includes(group.meMemberId) || expense.items?.some(item => item.memberIds.some(id => !memberIds.includes(id)))) throw new Error("Choose known group members for this expense.");
    // Validate FX even when native equals home; same-currency base rates must be 1.
    toBaseShares(expense, group.baseCurrency);
    const rememberedGroup: LocalSplitGroup = { ...group, ...fromWireSplitGroup({ ...group, rateHints: { ...group.rateHints, [expense.currency]: expense.rateNano }, homeRateNano: !previous && expense.homeRateNano !== null ? expense.homeRateNano : group.homeRateNano }) };
    const groupChanged = group.rateHints[expense.currency] !== expense.rateNano || group.homeRateNano !== rememberedGroup.homeRateNano;
    const nextGroup = groupChanged ? { ...rememberedGroup, updatedAt: splitStamp(group), ...DIRTY } : group;
    const nativeShare = computeShares(expense.amountMinor, expense.mode, expense.entries, expense.currency).get(group.meMemberId) ?? 0;
    const existingShares = await db.expenses.where("splitRef.splitExpenseId").equals(expense.id).toArray();
    if (existingShares.some(e => e.userId !== owner || e.splitRef?.groupId !== group.id)) throw new Error("Share expense belongs to another owner/group.");
    const canonical = await db.expenses.get(splitShareId(expense.id));
    if (canonical && (canonical.userId !== owner || canonical.splitRef?.splitExpenseId !== expense.id || canonical.splitRef.groupId !== group.id)) throw new Error("Share expense belongs to another owner/group.");
    const existing = canonical;
    const shareCurrency = existing?.deletedAt === null ? existing.splitRef?.homeCurrency ?? homeCurrency : homeCurrency;
    const profileChanged = shareCurrency !== homeCurrency;
    if (profileChanged && previous) expense.homeRateNano = previous.homeRateNano;
    const share = nativeShare === 0 ? cents(0) : profileChanged && existing ? existing.amountCents : myShareHome(expense, group.meMemberId, homeCurrency, expense.homeRateNano, group.baseCurrency, memberIds);
    assertShareAmount(share, shareCurrency);
    let shareExpense: LocalExpense | null = null;
    if (share > 0 && existing?.amountCents === share && existing.occurredAt === expense.occurredAt && existing.deletedAt === null && existing.splitRef?.homeCurrency === shareCurrency) {
      shareExpense = existing;
    } else if (share > 0) {
      const previousCategory = existing?.deletedAt === null ? await db.categories.get(existing.categoryId) : undefined;
      const isLiveCategory = (category: LocalCategory | undefined) => category && category.userId === owner && category.deletedAt === null && category.archivedAt === null && !isSavingsCategory(category);
      const category = isLiveCategory(previousCategory) ? previousCategory : await db.categories.get(options.categoryId);
      if (!isLiveCategory(category)) throw new Error("Choose a live personal expenditure category for your share.");
      const shareAt = splitStamp(existing);
      shareExpense = { ...existing, id: splitShareId(expense.id), userId: owner, categoryId: category!.id,
        amountCents: share, occurredAt: expense.occurredAt, note: existing ? existing.note : expense.description,
        recurringRuleId: existing?.recurringRuleId ?? null, sourceId: existing?.sourceId ?? null,
        splitRef: { groupId: group.id, splitExpenseId: expense.id, homeCurrency: shareCurrency, splitUpdatedAt: expense.updatedAt, groupKey: splitGroupKey(group) }, createdAt: existing?.createdAt ?? shareAt, updatedAt: shareAt, deletedAt: null, ...DIRTY };
      undo.expenses.push(canonical ?? splitTombstone(shareExpense));
      await db.expenses.put(shareExpense);
    } else if (existing && existing.deletedAt === null) {
      undo.expenses.push(existing);
      await db.expenses.put({ ...splitTombstone(existing), splitRef: { groupId: group.id, splitExpenseId: expense.id, homeCurrency: shareCurrency, splitUpdatedAt: expense.updatedAt, groupKey: splitGroupKey(group) } });
    }
    if (groupChanged) undo.groups.push(group);
    undo.splitExpenses.push(previous ?? splitTombstone(expense));
    await db.splitExpenses.put(expense);
    if (groupChanged) await db.splitGroups.put(nextGroup);
    return { expense, shareExpense };
  });
  queueSync();
  return { ...result, undo };
}

export async function deleteSplitExpense(id: string, ledger?: LedgerRef): Promise<SplitUndoSnapshot> {
  const { db, owner } = personalSplitStore(ledger);
  const undo = splitUndo();
  await db.transaction("rw", [db.splitGroups, db.splitExpenses, db.expenses], async () => {
    const expense = await db.splitExpenses.get(id);
    if (!expense || expense.userId !== owner) throw new Error("Split expense is unavailable or belongs to another owner.");
    await ownedSplitGroup(expense.groupId, ledger, true);
    undo.splitExpenses.push(expense);
    undo.expenses = await db.expenses.where("splitRef.splitExpenseId").equals(id).filter(e => e.deletedAt === null).toArray();
    if (undo.expenses.some(e => e.userId !== owner || e.splitRef?.groupId !== expense.groupId)) throw new Error("Share expense belongs to another owner/group.");
    await db.splitExpenses.put(splitTombstone(expense));
    for (const share of undo.expenses) await db.expenses.put(splitTombstone(share));
  });
  queueSync();
  return undo;
}

export type NewSplitTransfer = Omit<SplitTransfer, "id" | "userId" | "createdAt" | "updatedAt" | "deletedAt">;
export async function recordTransfer(input: NewSplitTransfer, ledger?: LedgerRef): Promise<{ transfer: LocalSplitTransfer; undo: SplitUndoSnapshot }> {
  return saveTransfer(input, undefined, ledger);
}
export async function updateTransfer(id: string, patch: Partial<Omit<NewSplitTransfer, "groupId">>, ledger?: LedgerRef): Promise<{ transfer: LocalSplitTransfer; undo: SplitUndoSnapshot }> {
  return saveTransfer(patch, id, ledger);
}
async function saveTransfer(input: Partial<NewSplitTransfer>, id?: string, ledger?: LedgerRef): Promise<{ transfer: LocalSplitTransfer; undo: SplitUndoSnapshot }> {
  const { db, owner } = personalSplitStore(ledger);
  const undo = splitUndo();
  const transfer = await db.transaction("rw", [db.splitGroups, db.splitTransfers], async () => {
    const previous = id ? await db.splitTransfers.get(id) : undefined;
    if (id && (!previous || previous.userId !== owner || previous.deletedAt !== null)) throw new Error("Transfer is unavailable or belongs to another owner.");
    const groupId = previous?.groupId ?? input.groupId;
    if (!groupId) throw new Error("Choose a split group for this transfer.");
    const group = await ownedSplitGroup(groupId, ledger);
    const updatedAt = splitStamp(previous);
    const row: LocalSplitTransfer = { ...previous, ...fromWireSplitTransfer({ ...previous, ...input, id: previous?.id ?? newId(), userId: owner, groupId,
      createdAt: previous?.createdAt ?? updatedAt, updatedAt, deletedAt: null }), ...DIRTY };
    const validation = validateTransfer({ ...row, memberIds: group.members.map(m => m.id) });
    if (!validation.ok) throw new Error(`${validation.reason}: ${validation.detail}`);
    undo.transfers.push(previous ?? splitTombstone(row));
    await db.splitTransfers.put(row);
    return row;
  });
  queueSync();
  return { transfer, undo };
}
export async function deleteTransfer(id: string, ledger?: LedgerRef): Promise<SplitUndoSnapshot> {
  const { db, owner } = personalSplitStore(ledger);
  const undo = splitUndo();
  await db.transaction("rw", [db.splitGroups, db.splitTransfers], async () => {
    const previous = await db.splitTransfers.get(id);
    if (!previous || previous.userId !== owner || previous.deletedAt !== null) throw new Error("Transfer is unavailable or belongs to another owner.");
    await ownedSplitGroup(previous.groupId, ledger);
    undo.transfers.push(previous);
    await db.splitTransfers.put(splitTombstone(previous));
  });
  queueSync();
  return undo;
}

export async function listSplitGroups(options: { includeArchived?: boolean } = {}, ledger?: LedgerRef): Promise<LocalSplitGroup[]> {
  const { db, owner } = personalSplitStore(ledger);
  return db.splitGroups.filter(g => g.userId === owner && g.deletedAt === null && (options.includeArchived === true || g.archivedAt === null)).toArray().then(groups => groups.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt) || a.id.localeCompare(b.id)));
}
export async function getSplitGroupDetail(groupId: string, ledger?: LedgerRef) {
  const { db, owner } = personalSplitStore(ledger);
  return db.transaction("r", [db.splitGroups, db.splitExpenses, db.splitTransfers], async () => {
    const group = await db.splitGroups.get(groupId);
    if (!group || group.userId !== owner || group.deletedAt !== null) return null;
    const expenses = await db.splitExpenses.where("groupId").equals(groupId).filter(e => e.userId === owner && e.deletedAt === null).toArray();
    const transfers = await db.splitTransfers.where("groupId").equals(groupId).filter(t => t.userId === owner && t.deletedAt === null).toArray();
    const { balances, skipped } = computeBalances(group, expenses, transfers);
    return { group, expenses, transfers, balances, skipped, skippedCount: skipped.length };
  });
}

/** Repair derived personal shares after a pull commits, without network work. */
export async function reconcileSplitShares(): Promise<number> {
  const { db, owner } = personalSplitStore();
  const tables = [db.splitGroups, db.splitExpenses, db.expenses, db.profile, db.categories, db.meta];
  async function plan() {
    const writes: LocalExpense[] = [];
    const unreadableDiagnostics: { key: string; value: string; cause: unknown }[] = [];
    let skipped = 0;
    const [splits, shares] = await Promise.all([
      db.splitExpenses.toArray(),
      db.expenses.where("splitRef.splitExpenseId").above("").filter(row => row.userId === owner && !row.splitRef?.linkId).toArray(),
    ]);
    if (splits.length === 0 && shares.length === 0) return { writes, skipped, unreadableDiagnostics };
    const groups = new Map((await db.splitGroups.toArray()).map(row => [row.id, row]));
    const splitMap = new Map(splits.map(row => [row.id, row]));
    const canonicalRows = new Map((await db.expenses.bulkGet(splits.map(row => splitShareId(row.id)))).filter(row => !!row).map(row => [row.id, row]));
    const profile = await db.profile.get(owner);
    const currency = profile?.currency ?? defaultProfile(owner, nowIso()).currency;
    const categories = (await db.categories.filter(row => row.userId === owner && row.deletedAt === null && row.archivedAt === null && !isSavingsCategory(row)).toArray()).sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
    const quarantined = new Set<string>();
    let unknownQuarantine = false;
    for (const record of await db.meta.where("key").startsWith("sync:quarantine:").toArray()) {
      try {
        const diagnostic: unknown = JSON.parse(record.value);
        if (!diagnostic || typeof diagnostic !== "object" || !("table" in diagnostic) || typeof diagnostic.table !== "string" || !("row_id" in diagnostic) || typeof diagnostic.row_id !== "string") throw new SplitRowError("Unreadable quarantine diagnostic.");
        if (diagnostic.table === "expenses") quarantined.add(diagnostic.row_id);
      } catch (cause) {
        if (!(cause instanceof SyntaxError || cause instanceof SplitRowError)) throw cause;
        unreadableDiagnostics.push({ ...record, cause });
        unknownQuarantine = true;
        skipped += 1;
      }
    }
    const countedQuarantines = new Set<string>();
    let defaultCategory: string | null | undefined;
    async function categoryFor(source: LocalExpense | undefined) {
      if (source && categories.some(row => row.id === source.categoryId)) return source.categoryId;
      if (defaultCategory === undefined) {
        const last = await db.expenses.orderBy("occurredAt").reverse().filter(row => row.userId === owner && row.deletedAt === null).first();
        defaultCategory = startCategoryId({ defaultId: profile?.defaultCategoryId ?? null, lastUsedId: last?.categoryId ?? null, live: categories });
      }
      return defaultCategory;
    }
    for (const split of splits) {
      const group = groups.get(split.groupId);
      if (split.userId !== owner || split.deletedAt !== null || !group || group.userId !== owner || group.deletedAt !== null) continue;
      try {
        const id = splitShareId(split.id);
        const source = canonicalRows.get(id);
        if (source && (source.userId !== owner || source.splitRef?.splitExpenseId !== split.id || source.splitRef.groupId !== group.id)) throw new SplitRowError("Open Splits to repair the share's owner or group.");
        if (quarantined.has(id)) {
          countedQuarantines.add(id);
          throw new SplitRowError("This share could not be read. Open Splits to repair it before retrying sync.");
        }
        if (!source && unknownQuarantine) continue;
        if (source?.deletedAt === null && source.splitRef?.splitUpdatedAt === split.updatedAt && source.splitRef.groupKey === splitGroupKey(group)) continue;
        assertSplitOccurredAt(split.occurredAt);
        const memberIds = group.members.map(member => member.id);
        const validation = validateSplit({ ...split, memberIds });
        if (!validation.ok || !memberIds.includes(split.payerMemberId)) throw new SplitRowError("Choose known group members and valid entries for this expense.");
        const frozen = source?.deletedAt === null && !!source.splitRef?.homeCurrency && source.splitRef.homeCurrency !== currency;
        const shareCurrency = frozen ? source.splitRef!.homeCurrency! : currency;
        const nativeShare = computeShares(split.amountMinor, split.mode, split.entries, split.currency).get(group.meMemberId) ?? 0;
        const amount = nativeShare === 0 ? cents(0) : frozen ? source.amountCents : myShareHome(split, group.meMemberId, currency, split.homeRateNano, group.baseCurrency, memberIds);
        assertShareAmount(amount, shareCurrency);
        const splitRef = { groupId: group.id, splitExpenseId: split.id, homeCurrency: shareCurrency, splitUpdatedAt: split.updatedAt, groupKey: splitGroupKey(group) };
        // Unchanged derived data must retain its LWW version, including old provenance.
        if (amount > 0 && source?.deletedAt === null && source.amountCents === amount && source.occurredAt === split.occurredAt && source.splitRef?.homeCurrency === shareCurrency) continue;
        if (amount > 0) {
          const categoryId = await categoryFor(source);
          if (!categoryId) throw new SplitRowError("Choose a live personal expenditure category for your share.");
          const at = splitStamp(source);
          writes.push({ ...source, id, userId: owner, categoryId, amountCents: amount, occurredAt: split.occurredAt,
            note: source ? source.note : split.description, recurringRuleId: source?.recurringRuleId ?? null, sourceId: source?.sourceId ?? null,
            splitRef, createdAt: source?.createdAt ?? at, updatedAt: at, deletedAt: null, ...DIRTY });
        } else if (source?.deletedAt === null) writes.push({ ...splitTombstone(source), splitRef });
      } catch (cause) {
        if (!(cause instanceof SplitRowError || cause instanceof MissingHomeRateError)) throw cause;
        skipped += 1;
      }
    }
    for (const share of shares) {
      const split = splitMap.get(share.splitRef!.splitExpenseId);
      const group = groups.get(share.splitRef!.groupId);
      const dead = split?.userId === owner && group?.userId === owner && (split.deletedAt !== null || group.deletedAt !== null);
      if (quarantined.has(share.id)) {
        if (!countedQuarantines.has(share.id)) skipped += 1;
        continue;
      }
      if (share.deletedAt === null && dead) writes.push(splitTombstone(share));
    }
    return { writes, skipped, unreadableDiagnostics };
  }
  const initial = await db.transaction("r", tables, plan);
  if (initial.writes.length === 0 && initial.unreadableDiagnostics.length === 0) return initial.skipped;
  const result = await db.transaction("rw", tables, async () => {
    const current = await plan();
    if (current.writes.length) await db.expenses.bulkPut(current.writes);
    return current;
  });
  for (const record of result.unreadableDiagnostics) {
    console.warn(`Could not read quarantine diagnostic ${record.key}:`, record.cause);
    try {
      await db.transaction("rw", db.meta, async () => {
        await db.meta.put({ key: record.key.replace("sync:quarantine:", "sync:quarantine-unreadable:"), value: record.value });
        await db.meta.delete(record.key); // invariant-exempt: local quarantine diagnostic moved to its unreadable key
      });
    } catch (cause) {
      console.warn(`Could not move unreadable quarantine diagnostic ${record.key}:`, cause);
    }
  }
  if (result.writes.length) queueSync();
  return result.skipped;
}

export { importLinkedShares } from "./linked_splits";
